import LoadSpinner from 'legacyfrontend/components/spinner';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import FlightCard from 'legacyfrontend/components/flights/FlightCard';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { useFlightsFilters } from 'legacyfrontend/contexts/FlightsFiltersContext';
import { useCallback, useEffect, useMemo, useState, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import PackagesApi from '../../../api/paquetes';
import Flex from '../../../components/Flex';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import { useRefreshModal } from 'legacyfrontend/hooks/useRefreshModal';
import RefreshModal from 'legacyfrontend/components/home/components/RefreshModal';
import { useSelector } from 'react-redux';
import { CACHE_DEFAULTS, SESSION_STORAGE_KEYS } from 'legacyfrontend/constants';
import { useColors } from 'legacyfrontend/hooks/useColors';

const FlightsListView = () => {
  const { t } = useTranslation('layout');
  const { onSearchCount, registerOnSearch } = useGlobal();
  const { setData, filteredData, isLoading, setIsLoading, error, setError, filters } =
    useFlightsFilters();
  const { search } = useLocation();
  const { flightInfo, setFlightInformation, handleCacheExpiration } = useFlightContext();
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const delegation = useSelector((state) => state.applicationState.delegation);
  const { showRefreshModal, setShowRefreshModal, handleRefreshConfirm } = useRefreshModal();
  const { background, text } = useColors();
  
  const hasCalledApiRef = useRef(false);

  const searchParamsToObject = useMemo(() => {
    if (!search) return {};

    const params = new URLSearchParams(search);
    const filters = {};

    params.forEach((value, key) => {
      if (key === 'passengers') {
        filters[key] = value.split(',').map(Number);
      } else {
        filters[key] = value;
      }
    });

    return filters;
  }, [search]);

  const hasValidSearchParams = useMemo(() => {
    if (!search) return false;

    const searchParams = new URLSearchParams(search);
    const hasOrigin = searchParams.get('originId') && searchParams.get('originName');
    const hasDestination = searchParams.get('destinationId') && searchParams.get('destinationName');
    const hasDepartureDate = searchParams.get('departureDate');

    return hasOrigin && hasDestination && hasDepartureDate;
  }, [search]);

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleLlegoData = useCallback(async () => {
    if (!hasValidSearchParams) {
      setData([]);
      setIsLoading(false);
      setError(false);
      return;
    }

    if (isLoading) {
      return;
    }

    setError(false);

    const {
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    } = searchParamsToObject;

    if (getCacheStatus() === true) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
    }

    const cacheValue = getCacheStatus();

    const data = {
      originId,
      destinationID: destinationId,
      origin: originName,
      destination: destinationName,
      passengers,
      departureDate,
      returnDate,
      direct: false,
      baggage: false,
      cabin: filters.cabinClass,
      currency: filters.currency,
      cache: cacheValue,
      delegation,
    };

    setFlightInformation({
      ...flightInfo,
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    });

    try {
      setIsLoading(true);
      const dataResponse = await PackagesApi.fetchLlegoData(data);
      setData(dataResponse?.data || []);
      handleCacheExpiration(
        dataResponse?.cacheExpiresAt ||
          new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME)
      );
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
      setError(false);
    } catch (error) {
      let errorMessage = 'Error al cargar los vuelos';

      if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'No se encontraron vuelos con los criterios especificados.';
      } else if (error?.code === 'ERR_NETWORK') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      setError(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasValidSearchParams,
    isLoading,
    searchParamsToObject,
    filters.cabinClass,
    filters.currency,
    delegation
  ]);

  const handleRefresh = useCallback(() => {
    handleLlegoData();
  }, [handleLlegoData]);

  useEffect(() => {
    hasCalledApiRef.current = false;
  }, [search]);

  const handleRetry = useCallback(async () => {
    if (!hasValidSearchParams) {
      return;
    }

    setError(false);
    setIsLoading(true);

    const {
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    } = searchParamsToObject;

    if (getCacheStatus() === true) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
    }

    const cacheValue = getCacheStatus();

    const data = {
      originId,
      destinationID: destinationId,
      origin: originName,
      destination: destinationName,
      passengers,
      departureDate,
      returnDate,
      direct: false,
      baggage: false,
      cabin: filters.cabinClass,
      currency: filters.currency,
      cache: cacheValue,
      delegation,
    };

    setFlightInformation({
      ...flightInfo,
      destinationId,
      destinationName,
      originId,
      originName,
      departureDate,
      returnDate,
      passengers,
    });

    try {
      const dataResponse = await PackagesApi.fetchLlegoData(data);
      setData(dataResponse?.data || []);
      handleCacheExpiration(
        dataResponse?.cacheExpiresAt ||
          new Date(Date.now() + CACHE_DEFAULTS.DEFAULT_EXPIRATION_TIME)
      );
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.CACHE_STATUS, 'true');
      setError(false);
    } catch (error) {
      let errorMessage = 'Error al cargar los vuelos';

      if (error?.response?.status === 500) {
        errorMessage = 'Error del servidor. Por favor, intenta nuevamente en unos momentos.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'No se encontraron vuelos con los criterios especificados.';
      } else if (error?.code === 'ERR_NETWORK') {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showError(errorMessage);
      setError(true);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    hasValidSearchParams,
    searchParamsToObject,
    filters.cabinClass,
    filters.currency,
    setFlightInformation,
    flightInfo,
    handleCacheExpiration,
    delegation,
    setData,
    setError,
    showError,
    setIsLoading
  ]);

  useEffect(() => {
    if (hasValidSearchParams && !isLoading && !hasCalledApiRef.current) {
      hasCalledApiRef.current = true;
      
      if (onSearchCount === 0) {
        registerOnSearch();
      }
      handleLlegoData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidSearchParams, search]);

  if (onSearchCount === 0 && !hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>Busca vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Completa el formulario de búsqueda para encontrar los mejores vuelos
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className='container flight__list-view__area loading-flights'
        style={{ textAlign: 'center', padding: '4rem 2rem' }}
      >
        <LoadSpinner />
        <h2 style={{ marginTop: '2rem' }}>Buscando los mejores vuelos para ti</h2>
        <p style={{ marginTop: '1rem', color: '#666' }}>Esto puede tomar unos segundos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--error'>
        <h2>¡Ups! Algo salió mal</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          No pudimos cargar los vuelos. Por favor, intenta nuevamente en unos momentos o ajuste los
          filtros de búsqueda.
        </p>
        <button 
          onClick={handleRetry}
          style={{
            backgroundColor: background,
            color: text,
            border: `2px solid ${background}`,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
            marginTop: '20px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = background;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = background;
            e.target.style.color = text;
          }}
        >
          🔄 Reintentar búsqueda
        </button>
        <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
      </div>
    );
  }

  if (!hasValidSearchParams) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Por favor, completa todos los campos de búsqueda para ver los vuelos disponibles.
        </p>
      </div>
    );
  }

  if (!filteredData || filteredData.length === 0) {
    return (
      <div className='container flight__list-view__area flight__list-view__area--empty'>
        <h2>No encontramos vuelos disponibles</h2>
        <p style={{ textAlign: 'center', padding: '0 2rem' }}>
          Prueba ajustando los filtros de búsqueda o seleccionando fechas diferentes.
        </p>
      </div>
    );
  }

  return (
    <div className='flight__list-view__area'>
      <Flex className='flight__list__view__wrapper--flight'>
        <Flex className='flight__list__view__wrapper--flight__results' vertical>
          {filteredData.map((flightData, index) => {
            if (!flightData || !flightData.flight || flightData.flight.length === 0) {
              return null;
            }
            return <FlightCard {...flightData} key={`${flightData.currency}-${index}`} />;
          })}
        </Flex>
      </Flex>
      <RefreshModal
        disableBackdropClick
        disableEscapeKeyDown
        onClose={() => setShowRefreshModal(false)}
        onConfirm={() => handleRefreshConfirm(handleRefresh)}
        open={showRefreshModal}
      />
      <ErrorSnackbar message={errorMessage} onClose={handleCloseToast} open={openToast} />
    </div>
  );
};

export default memo(FlightsListView);
